/**
 * Core fetch infrastructure shared by all api-client domain modules.
 * apiFetch and admobApi are exported so domain files can import them directly.
 */
import type { ApiResponse } from '@system/shared'

export const API_BASE = import.meta.env.VITE_API_URL ?? ''

export async function apiFetch<T>(path: string, options?: RequestInit): Promise<ApiResponse<T>> {
  try {
    const res = await fetch(`${API_BASE}${path}`, {
      credentials: 'include',
      ...options,
    })
    if (!res.ok) {
      // Try to read NestJS error body for a useful message; fallback to statusText.
      let message = res.statusText
      try {
        const body = await res.json() as Record<string, unknown>
        if (typeof body['message'] === 'string') message = body['message']
        // Lỗi kiểu { code } (exam: already_submitted, time_up_auto_submitted…): mã là thông điệp.
        else if (typeof body['code'] === 'string') message = body['code']
      } catch (_e) { /* non-JSON body — keep statusText */ }
      return { success: false, error: { code: String(res.status), message } }
    }
    return (await res.json()) as ApiResponse<T>
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : 'Network request failed'
    return { success: false, error: { code: 'NETWORK_ERROR', message } }
  }
}

/**
 * Generic authenticated fetch for the AdMob passthrough proxy at /api/admob/*.
 * The Node service returns raw JSON (not the {success,data} envelope), so this
 * wraps the response into ApiResponse<T> using the same error-handling pattern
 * as apiFetch, with credentials forwarded via session cookie.
 */
export async function admobApi<T>(path: string, opts?: { method?: string; body?: unknown }): Promise<ApiResponse<T>> {
  try {
    const res = await fetch(`${API_BASE}/api/admob${path}`, {
      credentials: 'include',
      method: opts?.method ?? 'GET',
      ...(opts?.body != null ? {
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(opts.body),
      } : {}),
    })
    if (!res.ok) {
      let message = res.statusText
      try {
        const body = await res.json() as Record<string, unknown>
        if (typeof body.message === 'string') message = body.message
        else if (typeof body.error === 'string' && body.error) message = body.error
        // `{ error: { message } }`: an error object instead of a flat message.
        else if (body.error && typeof body.error === 'object' && typeof (body.error as { message?: unknown }).message === 'string') message = (body.error as { message: string }).message
      } catch (_e) { /* non-JSON body */ }
      return { success: false, error: { code: String(res.status), message } }
    }
    const data = await res.json() as T
    return { success: true, data }
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : 'Network request failed'
    return { success: false, error: { code: 'NETWORK_ERROR', message } }
  }
}
